import * as React from "react";
import { ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { ClusterAnalyticsSummary } from "../Analytics/ClusterStatistics";
import { formatSeconds } from "../../../utils";
import { LiveChip } from "./LiveChip";
import { ClusterFrontendSchema } from "../../../api-client";

const InfoTooltip = ({ info, text }: { info: ReactNode; text?: any }) => (
  <Tooltip>
    <TooltipTrigger>
      <span className="inline whitespace-nowrap border-b border-dotted border-current cursor-help">
        {text}
      </span>
    </TooltipTrigger>
    <TooltipContent>
      <div>{info}</div>
    </TooltipContent>
  </Tooltip>
);

export const ClusterStatistics = ({
  cluster,
  analyticsSummary,
  clusterIsRunning,
}: {
  cluster?: ClusterFrontendSchema;
  analyticsSummary?: ClusterAnalyticsSummary;
  clusterIsRunning?: boolean;
}): React.ReactElement => {
  const diskDurationTotal =
    (analyticsSummary?.durationsTotal.diskRead
      ? analyticsSummary?.durationsTotal.diskRead
      : 0) +
    (analyticsSummary?.durationsTotal.diskWrite
      ? analyticsSummary?.durationsTotal.diskWrite
      : 0);

  const diskDurationText = (
    <>
      ({analyticsSummary?.durationsTotal.diskRead!.toFixed(2)}s read +{" "}
      {analyticsSummary?.durationsTotal.diskWrite!.toFixed(2)}s write) / <br />
      {analyticsSummary?.threadSeconds.toFixed(2)}s Available Thread Time.
      <br />
      <br />
      This is the amount of time that worker threads spent reading or writing to
      disk while running your tasks.
    </>
  );

  const computeDurationText = (
    <>
      {analyticsSummary?.durationsTotal.compute.toFixed(2)}
      s Compute Thread Time / <br />
      {analyticsSummary?.threadSeconds.toFixed(2)}s Available Thread Time.
      <br />
      <br />
      Compute Thread Time is the amount of time that worker threads spent
      running your tasks.
      <br />
      <br />
      100% would mean that every worker thread was running a computation for the
      entire time.
    </>
  );

  const idleText = (
    <>
      {analyticsSummary?.idleTimeout ? (
        <>
          {formatSeconds(analyticsSummary?.idleTimeout, 0, "short")} idle
          timeout
          <br />
        </>
      ) : (
        ""
      )}
      Idle since {analyticsSummary?.idleSince}
      <br />
      Last seen {analyticsSummary?.lastSeen}
    </>
  );
  // TODO: Migrate legacy ClusterData type to use camelizeKeys
  const stats: ([string, React.ReactElement] | null)[] = [
    ["Total Tasks", <>{analyticsSummary?.nTasks.toLocaleString()}</>],
    [
      "Uptime",
      <>
        {cluster?.billableTime
          ? formatSeconds(cluster.billableTime, 0, "short")
          : ""}
      </>,
    ],
    analyticsSummary?.threadSeconds
      ? [
          "Available Thread Time",
          <>
            <div className="flex flex-nowrap gap-2">
              <div className="flex-1">
                <InfoTooltip
                  text={
                    analyticsSummary?.threadSeconds &&
                    formatSeconds(analyticsSummary?.threadSeconds, 0, "short")
                  }
                  info="Wall Time × Number of Threads"
                />
              </div>
              <div className="flex-1">
                <div className="text-xs leading-4 whitespace-nowrap">
                  {analyticsSummary?.durationsTotal.compute &&
                  analyticsSummary?.threadSeconds ? (
                    <div className="flex flex-nowrap gap-0.5">
                      <div className="flex-1 text-muted-foreground">
                        Compute
                      </div>
                      <div className="text-right">
                        <InfoTooltip
                          text={`${(
                            (analyticsSummary?.durationsTotal.compute /
                              analyticsSummary?.threadSeconds) *
                            100
                          ).toFixed(2)}%`}
                          info={computeDurationText}
                        />
                      </div>
                    </div>
                  ) : null}
                  {analyticsSummary?.durationsTotal.compute &&
                  analyticsSummary?.threadSeconds ? (
                    <div className="flex flex-nowrap gap-0.5">
                      <div className="flex-1 text-muted-foreground">Disk</div>
                      <div className="text-right">
                        <InfoTooltip
                          text={`${(
                            (diskDurationTotal /
                              analyticsSummary?.threadSeconds) *
                            100
                          ).toFixed(2)}%`}
                          info={diskDurationText}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </>,
        ]
      : null,
    [
      "Used Credits",
      <>
        {cluster?.costPerHour && !cluster?.stopDesired ? (
          <InfoTooltip
            text={cluster?.totalCost?.toFixed(2)}
            info={`Current rate: ${cluster?.costPerHour?.toFixed(
              2,
            )} credits/hour`}
          />
        ) : (
          cluster?.totalCost?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })
        )}
      </>,
    ],
    [
      "Approx Cloud Cost",
      <>
        {cluster?.cloudCostPerHour && !cluster?.stopDesired ? (
          <InfoTooltip
            text={`$${cluster?.cloudTotalCost?.toFixed(2)}`}
            info={`Current rate: $${cluster?.cloudCostPerHour?.toFixed(
              2,
            )}/hour (approximate)`}
          />
        ) : cluster?.cloudTotalCost ? (
          `$${cluster?.cloudTotalCost?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`
        ) : (
          "–"
        )}
      </>,
    ],
    [
      "Last Idle For",
      <>
        {analyticsSummary?.idleDuration ? (
          <InfoTooltip
            text={formatSeconds(analyticsSummary?.idleDuration, 0, "short")}
            info={idleText}
          />
        ) : (
          "–"
        )}
      </>,
    ],
  ];

  return (
    <div className="flex gap-5 flex-nowrap">
      {stats
        .filter((value) => !!value)
        .map(([label, value]) => (
          <div key={label}>
            <div className="text-muted-foreground text-sm leading-5 mb-0.5">
              {label}
            </div>
            <div className="text-foreground text-xl font-medium leading-7">
              {value}
            </div>
          </div>
        ))}
      <div className="flex justify-end flex-1">
        {clusterIsRunning ? <LiveChip /> : null}
      </div>
    </div>
  );
};
